import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía del módulo PQRSDF de MiPueblo.
 *
 * PQRSDF = Peticiones, Quejas, Reclamos, Sugerencias, Denuncias y
 * Felicitaciones. Es el ejercicio del derecho fundamental de petición
 * (Constitución art. 23, Ley 1755/2015) por parte del ciudadano frente
 * a la administración.
 *
 * Este módulo es independiente del de Radicación de correspondencia
 * oficial (Ley 594/2000) — ver /docs/radicacion para esa pieza.
 *
 * Audiencia: admin_tenant + funcionarios que reciben, asignan y
 * responden PQRSDF.
 */
export function DocsPqrsdfPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · PQRSDF
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          PQRSDF — Derecho de petición del ciudadano
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 25-35 min. Para configurar
          dependencias, SLA, plantillas y routing por primera vez en un tenant
          cuenta 2-3 horas adicionales con el equipo jurídico y de servicio al
          ciudadano de la alcaldía.
        </div>

        <div className="callout tip">
          ¿Buscas <strong>correspondencia oficial</strong> entre entidades
          (oficios, circulares, memorandos)? Eso es otro módulo independiente
          regido por Ley 594/2000 →{" "}
          <Link to="/docs/radicacion">Ver Radicación de correspondencia</Link>.
        </div>

        {/* ---- 1. Qué es ---- */}
        <Section title="1. Qué es un PQRSDF">
          <p>
            Un PQRSDF es la materialización del{" "}
            <strong>derecho fundamental de petición</strong> consagrado en el
            artículo 23 de la Constitución Política y regulado por la{" "}
            <strong>Ley 1755 de 2015</strong>. Toda persona puede dirigirse a
            las autoridades para obtener pronta respuesta sobre asuntos de
            interés general o particular.
          </p>
          <p>
            MiPueblo soporta los <strong>6 tipos canónicos</strong> que la
            administración pública colombiana utiliza:
          </p>
          <Table
            head={["Tipo", "Qué es", "Ejemplo"]}
            rows={[
              [
                "Petición",
                "Solicitud respetuosa a la autoridad",
                "Pido copia del Acuerdo 012 de 2024",
              ],
              [
                "Queja",
                "Inconformidad por conducta de un servidor público",
                "El funcionario X me trató mal en ventanilla",
              ],
              [
                "Reclamo",
                "Inconformidad por la prestación de un servicio",
                "El alumbrado de la calle 5 lleva 2 meses dañado",
              ],
              [
                "Sugerencia",
                "Propuesta de mejora",
                "Sugiero habilitar pagos en línea del predial",
              ],
              [
                "Denuncia",
                "Noticia de presunta irregularidad o falta",
                "Denuncio mal uso de recursos en la obra del parque",
              ],
              [
                "Felicitación",
                "Reconocimiento positivo",
                "Felicito la atención recibida en hacienda",
              ],
            ]}
          />
          <div className="callout">
            En código se modela como un <code>Literal</code>:{" "}
            <code>"peticion" | "queja" | "reclamo" | "sugerencia" |
              "denuncia" | "felicitacion"</code>{" "}
            (ver{" "}
            <code>apps/backend/src/mipueblo/api/publico/radicar.py</code>).
          </div>
        </Section>

        {/* ---- 2. Plazos ---- */}
        <Section title="2. Plazos legales de respuesta">
          <p>
            La Ley 1755/2015 fija plazos máximos en <strong>días hábiles</strong>{" "}
            (incluye festivos colombianos al calcular). MiPueblo los aplica por
            defecto y permite ajustarlos por tenant en <code>/sla</code>.
          </p>
          <Table
            head={["Subtipo", "Plazo Ley 1755", "Norma"]}
            rows={[
              ["Petición general", "15 días hábiles", "Art. 14 Ley 1755/2015"],
              [
                "Petición de información o documentos",
                "10 días hábiles",
                "Art. 14.1 Ley 1755/2015",
              ],
              [
                "Petición de consulta (concepto jurídico)",
                "30 días hábiles",
                "Art. 14.2 Ley 1755/2015",
              ],
              ["Queja / Reclamo", "15 días hábiles", "Art. 14 Ley 1755/2015"],
              [
                "Tutela (notificación de fallo)",
                "Lo que defina el juez (típico 48h)",
                "Decreto 2591/1991",
              ],
              [
                "Emergencias / pandemia",
                "Ampliación hasta el doble",
                "Decreto 491/2020",
              ],
            ]}
          />
          <div className="callout warn">
            El silencio administrativo positivo o negativo es{" "}
            <strong>sancionable</strong> (Procuraduría / SIC). Si no se va a
            cumplir el plazo, hay que enviar al ciudadano una{" "}
            <strong>comunicación de prórroga</strong> motivada antes de que
            venza.
          </div>
        </Section>

        {/* ---- 3. Cómo radica el ciudadano ---- */}
        <Section title="3. Cómo radica el ciudadano (flujo público)">
          <p>
            El ciudadano radica <strong>sin login</strong>. El tenant se
            resuelve por subdominio (<code>cajica.mipuebloapp.com</code>) y el
            endpoint público es{" "}
            <code>POST /publico/radicar</code> (ver{" "}
            <code>apps/backend/src/mipueblo/api/publico/radicar.py</code>).
          </p>
          <h4 style={{ marginTop: 18 }}>3.1 Canales disponibles</h4>
          <Ul
            items={[
              <>
                <strong>Portal web</strong> →{" "}
                <code>tenant.mipuebloapp.com/radicar</code>
              </>,
              <>
                <strong>WhatsApp Cloud API</strong> — bot conversacional que
                arma el PQRSDF y lo entrega con número (ver{" "}
                <Link to="/docs/whatsapp">guía de WhatsApp</Link>)
              </>,
              <>
                <strong>Email</strong> — buzón unificado del tenant
              </>,
              <>
                <strong>Telegram / Messenger / Instagram</strong> — si están
                habilitados en el tenant
              </>,
              <>
                <strong>Presencial / Teléfono</strong> — un funcionario captura
                el PQRSDF a nombre del ciudadano (ver §6)
              </>,
            ]}
          />

          <h4 style={{ marginTop: 18 }}>3.2 Paso a paso en el portal web</h4>
          <Ol>
            <li>
              Aceptar el <strong>tratamiento de Habeas Data</strong>{" "}
              (obligatorio si entrega datos personales — la API rechaza con 422
              si <code>consentimiento_habeas_data = false</code>).
            </li>
            <li>
              Llenar peticionario: tipo y número de documento (CC, CE, TI, NIT,
              pasaporte, otro), nombre, email, teléfono. Si ya existió antes
              con el mismo documento, MiPueblo lo <strong>reutiliza</strong> y
              renueva la fecha de consentimiento.
            </li>
            <li>Elegir uno de los 6 tipos y redactar asunto + contenido.</li>
            <li>
              Adjuntar hasta 5 archivos (JPG, PNG, WebP, PDF — máx 16 MB cada
              uno; configurable en <code>/radicar/limites</code>).
            </li>
            <li>
              Resolver el <strong>captcha Cloudflare Turnstile</strong>{" "}
              (validado server-side por <code>verify_turnstile</code>).
            </li>
            <li>
              Click <Kbd>Radicar</Kbd> → respuesta:
              <Ul
                items={[
                  <>
                    <code>radicado</code> en formato{" "}
                    <code>SLUG-AÑO-NNNNN</code> (ej.{" "}
                    <code>CAJICA-2026-00873</code>)
                  </>,
                  <>
                    <code>seguimiento_token</code> +{" "}
                    <code>seguimiento_url</code> para consultar estado
                  </>,
                  <>
                    <code>fecha_limite_respuesta</code> ya calculada por SLA
                  </>,
                ]}
              />
            </li>
          </Ol>
          <div className="callout tip">
            El ciudadano puede consultar el estado en <code>/consultar</code>{" "}
            usando radicado + documento, o seguir el link único que MiPueblo le
            envía por su canal.
          </div>
        </Section>

        {/* ---- 4. Anonimato y Habeas Data ---- */}
        <Section title="4. Anonimato y consentimiento Habeas Data">
          <p>
            La Ley 1755/2015 permite radicar PQRSDF de forma{" "}
            <strong>anónima</strong> (la administración debe responderlos
            igualmente, salvo cuando la identidad sea indispensable). En
            MiPueblo:
          </p>
          <Ul
            items={[
              <>
                Si el ciudadano <strong>no quiere</strong> entregar datos →
                radica anónimo y omite el bloque de peticionario. El campo{" "}
                <code>peticionario</code> queda <code>None</code> y{" "}
                <code>es_anonimo = true</code>.
              </>,
              <>
                Si entrega datos personales → <strong>obligatorio</strong>{" "}
                aceptar el tratamiento de datos (<strong>Ley 1581/2012</strong>
                , Decreto 1377/2013). MiPueblo guarda{" "}
                <code>consentimiento_habeas_data</code>,{" "}
                <code>consentimiento_fecha</code> y la IP de origen.
              </>,
              <>
                La anonimización es <strong>irreversible</strong>: una vez
                radicado anónimo no se puede agregar peticionario después.
              </>,
              <>
                Para denuncias sensibles (corrupción, acoso), recomendar el
                modo anónimo y avisar que la respuesta se publica por el canal
                de seguimiento, no por contacto directo.
              </>,
            ]}
          />
        </Section>

        {/* ---- 5. Recepción en bandeja ---- */}
        <Section title="5. Recepción en bandeja del funcionario">
          <p>
            Cada PQRSDF llega a la <strong>bandeja del tenant</strong> (
            <code>/bandeja</code> en el tenant-app). La bandeja filtra
            automáticamente por permisos del usuario y por reglas RLS de
            Postgres — un funcionario solo ve los PQRSDF de las dependencias a
            las que pertenece (ver{" "}
            <code>domain/permisos.py::user_can_see_pqrsdf</code>).
          </p>
          <Ul
            items={[
              <>
                Vista de tarjetas y tabla con: número, tipo, asunto, canal,
                estado, prioridad, dependencia, asignado, SLA restante.
              </>,
              <>
                Filtros por estado, dependencia, canal, prioridad, rango de
                fechas y texto libre.
              </>,
              <>
                Badges de SLA: verde (sobra tiempo), ámbar (24h o menos),
                rojo (vencido).
              </>,
              <>
                Click en una tarjeta → <code>/radicado/:id</code> con
                detalle completo, hilo de eventos y acciones.
              </>,
            ]}
          />
        </Section>

        {/* ---- 6. Asignación ---- */}
        <Section title="6. Asignación a dependencia">
          <p>
            Un PQRSDF pertenece a una <strong>dependencia</strong> (Secretaría
            de Hacienda, Planeación, etc.) y opcionalmente a un{" "}
            <strong>funcionario específico</strong> dentro de ella. Dos modos:
          </p>
          <Ul
            items={[
              <>
                <strong>Manual</strong> — el funcionario de ventanilla o
                supervisor elige la dependencia al radicar / desde el detalle.
              </>,
              <>
                <strong>Automática (routing rules)</strong> — si el tenant
                configuró reglas en <code>/routing</code>, MiPueblo asigna
                según palabras clave del asunto/contenido, tipo o canal. La
                aplicación corre en{" "}
                <code>domain/routing.py::apply_routing</code> justo después de
                crear el PQRSDF.
              </>,
            ]}
          />
          <p>
            La asignación puntual a un funcionario dentro de la dependencia
            ocurre <strong>después</strong>, desde el detalle del radicado.
          </p>
        </Section>

        {/* ---- 7. Estados ---- */}
        <Section title="7. Estados del PQRSDF (máquina de estados)">
          <p>
            La máquina de estados es <strong>estricta</strong> — las
            transiciones inválidas devuelven 400. Se define en{" "}
            <code>api/funcionario/pqrsdf.py::_VALID_TRANSITIONS</code>.
          </p>
          <Table
            head={["Estado", "Significado", "Transiciones permitidas"]}
            rows={[
              [
                "radicado",
                "Recién creado, sin tocar",
                "→ en_proceso, → archivado",
              ],
              [
                "en_proceso",
                "Alguien lo asumió o respondió parcialmente",
                "→ respondido, → archivado",
              ],
              [
                "respondido",
                "Se envió respuesta oficial al ciudadano",
                "→ cerrado, → en_proceso (réplica)",
              ],
              [
                "cerrado",
                "Sin más interacciones, queda en expediente",
                "→ archivado",
              ],
              [
                "archivado",
                "Terminal — solo lectura archivística",
                "(ninguna)",
              ],
            ]}
          />
          <div className="callout">
            Los cambios de estado emiten un evento firmado en la cadena de
            <code>RadicadoEvento</code>, verificable con{" "}
            <code>domain/eventos.py::verify_chain</code>.
          </div>
        </Section>

        {/* ---- 8. Trasladar ---- */}
        <Section title="8. Trasladar a otra dependencia">
          <p>
            Si la dependencia receptora considera que el PQRSDF{" "}
            <strong>no es de su competencia</strong>, puede trasladarlo (Art.
            21 Ley 1755/2015 — el plazo se cuenta desde el traslado).
          </p>
          <Ol>
            <li>
              Detalle del radicado → <Kbd>Trasladar</Kbd>.
            </li>
            <li>
              Elegir <strong>nueva dependencia</strong> y escribir un{" "}
              <strong>motivo</strong> (queda en la auditoría inmutable).
            </li>
            <li>
              MiPueblo:
              <Ul
                items={[
                  <>Limpia la asignación individual (queda sin funcionario).</>,
                  <>
                    Si estaba en <code>radicado</code> lo pasa a{" "}
                    <code>en_proceso</code>.
                  </>,
                  <>
                    Emite evento <code>traslado</code> firmado en la cadena de
                    eventos.
                  </>,
                  <>
                    <strong>Notifica al ciudadano</strong> por su canal
                    original con el motivo (vía{" "}
                    <code>peticionario_notify</code>).
                  </>,
                ]}
              />
            </li>
          </Ol>
          <div className="callout warn">
            No se puede trasladar un PQRSDF en <code>cerrado</code> ni{" "}
            <code>archivado</code>.
          </div>
        </Section>

        {/* ---- 9. Responder ---- */}
        <Section title="9. Responder al ciudadano">
          <p>
            La pantalla <code>RadicadoResponder.tsx</code> reúne la redacción,
            adjuntos, plantillas, borrador con IA y firma.
          </p>
          <Ul
            items={[
              <>
                <strong>Editor enriquecido</strong> (HTML) — se convierte a
                texto plano para el canal cuando aplica (
                <code>html_to_text</code>).
              </>,
              <>
                <strong>Plantillas reutilizables</strong> (
                <code>ResponseTemplate</code>) por dependencia, con variables
                tipo <code>{`{{ciudadano.nombre}}`}</code>.
              </>,
              <>
                <strong>Borrador con IA</strong> — <code>generate_draft</code>{" "}
                propone una respuesta basada en el contenido del PQRSDF, el
                contexto del tenant y el conocimiento (KB) cargado. El
                funcionario lo revisa y edita; la IA <strong>nunca</strong>{" "}
                envía sola.
              </>,
              <>
                <strong>Adjuntos de salida</strong> — PDFs, escaneos, soportes
                (<code>save_outbound_attachment</code>).
              </>,
              <>
                <strong>Firma digital PAdES</strong> sobre el PDF de respuesta
                generado por <code>render_respuesta_pdf_async</code> (formato
                aprobado por la SIC para documentos públicos).
              </>,
              <>
                <strong>Envío multicanal</strong>: el mismo cuerpo se entrega
                por el canal original del ciudadano (WhatsApp, email, Telegram,
                Messenger, Instagram), con el PDF firmado adjunto cuando
                aplica.
              </>,
            ]}
          />
        </Section>

        {/* ---- 10. Notificación al ciudadano ---- */}
        <Section title="10. Notificación al ciudadano y canal de respuesta">
          <p>
            MiPueblo responde por el <strong>mismo canal</strong> por el que
            entró el PQRSDF, salvo que el ciudadano pida explícitamente otro
            (por ejemplo radicó por web pero pide respuesta por correo
            certificado).
          </p>
          <Table
            head={["Canal de entrada", "Canal de respuesta", "Soporte legal"]}
            rows={[
              [
                "WhatsApp",
                "WhatsApp + PDF firmado adjunto",
                "Comunicación electrónica Ley 527/1999",
              ],
              [
                "Email",
                "Email con PDF firmado + acuse",
                "Comunicación electrónica Ley 527/1999",
              ],
              [
                "Portal web",
                "Email si dejó email, sino consulta vía seguimiento_token",
                "Notificación electrónica Art. 56 CPACA",
              ],
              [
                "Presencial",
                "Email/WhatsApp si dejó contacto, sino oficio físico",
                "Notificación personal Art. 67 CPACA",
              ],
              [
                "Anónimo",
                "Solo consulta pública por radicado",
                "Art. 16 Ley 1755/2015",
              ],
            ]}
          />
        </Section>

        {/* ---- 11. SLA dashboard ---- */}
        <Section title="11. SLA — dashboard y alertas">
          <p>
            El plazo se calcula al radicar (
            <code>domain/sla.py::calculate_deadline</code>) en{" "}
            <strong>días hábiles colombianos</strong> usando el calendario de
            festivos del año. El monitor envía recordatorios automáticos:
          </p>
          <Ul
            items={[
              <>
                <strong>72h antes</strong> del vencimiento — recordatorio al
                funcionario asignado.
              </>,
              <>
                <strong>24h antes</strong> — alerta al supervisor de la
                dependencia.
              </>,
              <>
                <strong>Al vencer</strong> — escalamiento a admin_tenant +
                marca de SLA vencido en reportes.
              </>,
              <>
                Dashboard ejecutivo en <code>/dashboard</code> con SLA por
                dependencia, top 5 vencidos, tiempo promedio de respuesta.
              </>,
            ]}
          />
        </Section>

        {/* ---- 12. Tutela ---- */}
        <Section title="12. Tutela y aceleración de plazos">
          <p>
            Cuando un ciudadano interpone una <strong>acción de tutela</strong>{" "}
            relacionada con un PQRSDF, MiPueblo permite marcarlo con
            prioridad <code>tutela</code>:
          </p>
          <Ul
            items={[
              <>
                Pasa al tope de la bandeja con badge rojo distintivo.
              </>,
              <>
                El SLA se recalcula al <strong>plazo del juez</strong> (típico
                48h hábiles).
              </>,
              <>
                Notificación inmediata a admin_tenant + supervisor + asignado.
              </>,
              <>
                Vinculable con el número de radicado de tutela del juzgado para
                trazabilidad cruzada.
              </>,
              <>
                El evento queda en cadena firmada para evidencia probatoria.
              </>,
            ]}
          />
        </Section>

        {/* ---- 13. Encuesta ---- */}
        <Section title="13. Encuesta de satisfacción post-respuesta">
          <p>
            Al pasar a <code>respondido</code>, MiPueblo encola automáticamente
            una <strong>encuesta de satisfacción</strong> (
            <code>create_encuesta_for_respuesta</code> +{" "}
            <code>send_encuesta_via_channel</code>):
          </p>
          <Ul
            items={[
              <>
                Se envía 24h después por el mismo canal del ciudadano (para
                evitar interrumpir la lectura de la respuesta).
              </>,
              <>
                3 preguntas: claridad de la respuesta (1-5), tiempo (1-5),
                comentario libre opcional.
              </>,
              <>
                Configurable por tenant — se puede desactivar o cambiar las
                preguntas en <code>/encuestas</code>.
              </>,
              <>
                Resultados agregados alimentan el dashboard de calidad de
                servicio.
              </>,
            ]}
          />
        </Section>

        {/* ---- 14. Reportes ---- */}
        <Section title="14. Reportes regulatorios">
          <p>
            Disponibles desde <code>/reportes</code> del tenant. Cumplen
            estándares de la Procuraduría, SIC y DAFP.
          </p>
          <Ul
            items={[
              <>
                <strong>Consolidado mensual por tipo</strong> — formato
                compatible con reporte SUI / Procuraduría.
              </>,
              <>
                <strong>SLA cumplido vs vencido</strong> por dependencia y por
                periodo.
              </>,
              <>
                <strong>Tutelas atendidas</strong> con tiempos y resultado.
              </>,
              <>
                <strong>Encuestas de satisfacción</strong> — agregado por
                trimestre.
              </>,
              <>
                <strong>Canales más usados</strong> por el ciudadano.
              </>,
              <>
                Exportable a CSV / XLSX / PDF firmado.
              </>,
            ]}
          />
        </Section>

        {/* ---- 15. Buenas prácticas ---- */}
        <Section title="15. Buenas prácticas y errores comunes">
          <Checklist
            items={[
              <>
                <strong>Capacitar a ventanilla</strong> en la distinción PQRSDF
                vs correspondencia oficial. Si quien escribe es un ciudadano
                persona-natural ejerciendo derecho de petición → PQRSDF. Si es
                otra entidad o particular formal vía oficio →{" "}
                <Link to="/docs/radicacion">correspondencia</Link>.
              </>,
              <>
                <strong>Responder SIEMPRE dentro del SLA</strong>, aunque sea
                con prórroga motivada (Decreto 491/2020). El silencio es
                sancionable.
              </>,
              <>
                <strong>No archivar para "limpiar"</strong> la bandeja — solo
                tras cerrar formalmente. El archivado es terminal y archivo
                inmutable.
              </>,
              <>
                <strong>Verificar consentimiento Habeas Data</strong> cuando un
                funcionario radica a nombre del ciudadano: pedir firma del
                formulario físico y adjuntar.
              </>,
              <>
                <strong>Borrador IA siempre revisado</strong> antes de enviar.
                La IA no firma respuestas oficiales sola — la responsabilidad
                es del funcionario asignado.
              </>,
              <>
                <strong>Trasladar rápido</strong> si no es de tu competencia
                (máx 5 días hábiles según Art. 21 Ley 1755), no &quot;guardar
                para después&quot;.
              </>,
              <>
                Revisar mensualmente el reporte de <strong>SLA vencidos</strong>{" "}
                con el equipo directivo de la alcaldía.
              </>,
              <>
                Para denuncias sensibles, recomendar canal anónimo y verificar
                que la respuesta no exponga datos del peticionario.
              </>,
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13.5,
        }}
      >
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{
                borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)",
              }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ExtLink exportado para coherencia con otros docs aunque no se use aquí.
void ExtLink;
